<template>
  <div class="page-header-links">
    <template v-if="showContact">
      <PageCountrySelect :country="country" @change="onChangeCountry"></PageCountrySelect>
      <div class="header-link-divider">|</div>
    </template>
    <div v-if="showSearchIcon" class="page-header-search">
      <a :href="searchUrl" class="header-link" aria-label="Search" title="Search" @click.prevent="toggleSearch"><span class="af af-search"></span></a>
    </div>
    <div v-if="showSearchIcon" class="header-link-divider">|</div>
    <template v-if="showContact">
      <AboutLink class="header-link" :about-url="aboutUrl"></AboutLink>
      <div class="header-link-divider">|</div>
      <ChatLink class="header-link" :chat-url="chatUrl" @chat="chat"></ChatLink>
      <div class="header-link-divider">|</div>
      <PhoneLink class="header-link"></PhoneLink>
      <div class="header-link-divider">|</div>
    </template>
    <div class="page-header-account">
      <a :href="accountUrl" class="header-link" aria-label="Account" title="Account"><i class="af af-account"></i></a>
    </div>
    <div class="header-link-divider">|</div>
    <div class="page-header-favorites">
      <a :href="favoritesUrl" class="header-link" aria-label="Favorites" title="Favorites">
        <i class="af af-heart"></i>
        <span v-if="favoritesCount" class="page-header-favorites-count">{{ favoritesCount }}</span>
      </a>
    </div>
    <div class="header-link-divider">|</div>
    <div v-click-outside="closeCart" class="page-header-cart">
      <a :href="cartUrl" class="header-link" aria-label="Cart" title="Cart" @click.prevent="toggleCart">
        <i class="af af-cart"></i>
        <span class="page-header-cart-count" :style="{ visibility: cartCount ? 'visible' : 'hidden' }">{{ cartCount }}</span>
      </a>
      <transition v-if="delayShow" name="cart-fade">
        <div v-show="showCart" class="mini-cart-container " :class="{ 'mini-cart-menu-right': showMenuIcon, 'new': useNewMiniCart  }">
          <NewMiniCart v-if="useNewMiniCart" :cart="cart" @checkout="checkout" @closeMiniCart="closeCart" :isShown="showCart" :cartFetched="cartFetched"></NewMiniCart>
          <MiniCart v-else="!useNewMiniCart" :cart="cart" @checkout="checkout" @closeMiniCart="closeCart" :isShown="showCart" :cartFetched="cartFetched"></MiniCart>
        </div>
      </transition>
    </div>
    <div v-if="showMenuIcon" class="page-header-menu">
      <a v-if="!showMenuClose" class="header-link" aria-label="Open Menu" tabindex="0" @click.prevent="openMenu"><i class="af af-menu"></i></a>
      <a v-else class="header-link" aria-label="Close Menu" tabindex="0" @click.prevent="closeMenu"><i class="af af-close"></i></a>
    </div>
  </div>
</template>
<script>
import ClickOutside from 'vue-click-outside';

import AboutLink from './AboutLink.vue';
import ChatLink from './ChatLink.vue';
import PhoneLink from './PhoneLink.vue';
import PageCountrySelect from './PageCountrySelect.vue';
import NewMiniCart from '@/components/cart/NewMiniCart.vue';

import Chronicle from '@/components/commons/Chronicle.js';
import Tracking from '@/components/commons/Tracking.js';

export default {
  components: {
    PageCountrySelect,
    AboutLink,
    ChatLink,
    PhoneLink,
    NewMiniCart,
    MiniCart: () => import(/* webpackChunkName: "MiniCart" */ '@/components/cart/MiniCart.vue'),
  },

  directives: {
    ClickOutside,
  },

  props: [
    'showCart',
    'showContact',
    'showMenuClose',
    'showMenuIcon',
    'showSearchIcon',
    'country',
    'searchUrl',
    'aboutUrl',
    'chatUrl',
    'accountUrl',
    'favoritesUrl',
    'cartUrl',
    'cart',
    'cartCount',
    'favoritesCount',
    'cartFetched',
  ],

  data() {
    return {
      useNewMiniCart: false,
      delayShow: false,
    };
  },

  beforeMount() {

  },

  mounted() {
    if ('requestIdleCallback' in window) {
      this.delayShow = true;
    } else {
      setTimeout(() => {
        this.delayShow = true;
      });
    }

    this.checkIfFlyOutMiniCartIsEnabled()
  },

  methods: {
    enableCloseOnScroll() {
      if(this.useNewMiniCart) { return }
      const body = document.querySelector('body');

      if (body) {
        body.addEventListener('touchmove', () => this.closeCart());
      }
    },

    checkIfFlyOutMiniCartIsEnabled() {
      if(Chronicle.loaded) {
        this.useNewMiniCart = Chronicle.getFeatureStatus('fly-out-enabled-2') === 'enabled';
        this.enableCloseOnScroll()
      }else{
        setTimeout(() => {
          this.checkIfFlyOutMiniCartIsEnabled()
        }, 500)
      }
    },

    openMenu() {
      this.$emit('openMenu');
    },

    closeMenu() {
      this.$emit('closeMenu');
    },

    toggleCart() {
      const variant = Chronicle.getFeatureStatus('fly-out-enabled-2') === 'enabled';
      Tracking.track('mini_cart_open', { mini_cart_variant: variant });

      this.$emit('toggleCart');
    },

    closeCart() {
      if (this.showCart) {
        this.$emit('closeCart');
      }
    },

    toggleSearch() {
      this.$emit('toggleSearch');
    },

    chat() {
      this.$emit('chat');
    },

    checkout() {
      this.$emit('checkout');
    },

    onChangeCountry(country) {
      this.$emit('changeCountry', country);
    },
  },
};
</script>

<style lang="scss">
@import '~@article/pixel/dist/style/variables';

.page-header-links {
  display: flex;
  align-items: center;
  flex-grow: 1;
  justify-content: flex-end;

  a,
  a:visited,
  a:focus,
  a:active {
    color: var(--font-color);
  }

  .page-header-cart,
  .page-header-favorites {
    user-select: none;
    position: relative;
  }

  & > * {
    display: none;
    margin-left: 30px;

    @media (min-width: $tablet-breakpoint) {
      display: block;
      margin-left: 0px;
    }

    @media (max-width: $small-breakpoint - 1) {
      margin-left: 25px;
    }
  }

  .header-link-divider {
    margin-left: 15px;
    margin-right: 15px;
  }

  .header-link-divider,
  .header-link {
    touch-action: manipulation;
    font-size: var(--font-size);

    @media (min-width: $tablet-breakpoint) {
      font-size: var(--font-size-14);
    }
  }

  .page-header-search,
  .page-header-cart,
  .page-header-menu {
    display: block;
  }

  .page-header-menu {
    @media (min-width: $tablet-breakpoint) {
      display: none;
    }
  }

  .page-header-cart-count,
  .page-header-favorites-count {
    top: 0px;
    right: -10px;
    width: 14px;
    height: 14px;
    line-height: 16px;
    border-radius: 50%;
    position: absolute;
    font-family: var(--font-bold);
    font-size: 0.5em;
    color: var(--white);
    background-color: var(--accent-color);
    text-align: center;
  }

  .mini-cart-container {
    position: absolute;
    opacity: 1;
    right: -12px;
    top: 38px;
    z-index: 1;
    transition: all 150ms ease-out;

    &.new{
      top: 0;
      position: fixed;
      z-index: 9;
    }

    @media (max-width: $desktop-breakpoint - 1) {
      right: -12px;
    }

    @media (max-width: $tablet-breakpoint - 1) {
      right: -24px;
      top: 41px;
    }

    @media (max-width: $small-breakpoint - 1) {
      left: calc(40px - 100vw);
      right: 0px;
      top: 41px;
    }

    &.mini-cart-menu-right {
      @media (max-width: $tablet-breakpoint - 1) {
        right: -66px;
        top: 41px;
      }

      @media (max-width: $small-breakpoint - 1) {
        left: calc(77px - 100vw);
        right: 0px;
        top: 41px;
      }
    }

    &.cart-fade-enter,
    &.cart-fade-leave-to {
      opacity: 0;
      top: 30px;
    }

    &.new{
        @media (max-width: $small-breakpoint - 1) {
            width: 100vw;
            left: 0;
        }

        right: 0;

        &.cart-fade-enter,
        &.cart-fade-leave-to {
          opacity: 0;
            top: 0px;
            right: -40px;
            @media (max-width: 760px) {
                top: 40px;
                right: 0;
            }
        }

    }
  }

  .count-change-enter,
  .count-change-leave-to {
    transform: scale(1.1);
  }
}
</style>
